import { alternatePassesFilters, filterLineItemsByAlternates, hasAlternateFilters } from "./helpers/alternateFilters";
import {
  BLANK_LABEL,
  ColumnNameToIndexMap,
  codeLabel,
  getReportColumnCount,
  getReportOptions,
  updateReportDate,
} from "./helpers/common";
import {
  PreservedContext,
  applyPreservedValuesToRow,
  getPreservedColumnValues,
  getPreservedKeyColumnIndices,
  shouldUsePreservedValues,
} from "./helpers/preservedColumns";
import { createColumnNameToIndexMap, printFormattedData } from "./helpers/print";
import { cacheableData, worksheetCache } from "../workbook/cache";
import { track } from "../../clients/mixpanel";
import { ValidationError } from "../../errors";
import { getEstimateReportData, setPrintAreaToUsedRange } from "../workbook";
import { getProcessedEstimate } from "../datasources/estimate";
import { LineItem } from "../../interfaces/LineItem";
import { columnNames, rowNames } from "./helpers/rangeNames";
import { getSOVGroupings } from "./helpers/groupings";
import { handleDynamicColumns } from "./helpers/print";
import { ensureStylesExist } from "../styles/worksheet";
import { transformUseGroups } from "../datasources/useGroups";
import { ReportUseGroup } from "../../interfaces/EstimateUseGroup";
import { FloorGroup } from "../../interfaces/AreaSheet";
import { splitLineItemsForSOV } from "./sov";

export interface AlternatesSOVReportOptions extends cacheableData {
  groupBy: string;
  freezeGroupLevels: boolean;
  excludeAccepted: boolean;
  excludeRejected: boolean;
  filterByType: string;
  /** Prefix VM decision column headers with their code. */
  showCodesColumns: boolean;
  /** Prefix each row's name with its code. Keeps the pre-split key so existing sheets carry over. */
  showCodes: boolean;
  /** When grouped by use group or building, split multi-use line items across their groups. */
  splitByUse: boolean;
}

export const alternatesSOVDefaultOptions = {
  freezeGroupLevels: false,
  excludeAccepted: false,
  excludeRejected: false,
  filterByType: "",
  showCodesColumns: true,
  showCodes: false,
  splitByUse: false,
  // allocateMarkups: true,
} as AlternatesSOVReportOptions;

export const generateAlternatesSOVReport = async (worksheet: Excel.Worksheet) => {
  const startTime = performance.now();
  const options = (await getReportOptions(worksheet, alternatesSOVDefaultOptions)) as AlternatesSOVReportOptions;

  const reportData = (await getEstimateReportData()) as any[];
  const estimateId = reportData[0]?.estimate_id as string | undefined;
  if (!estimateId) {
    throw new ValidationError("Load an estimate before generating a report.");
  }

  const processed = await getProcessedEstimate(estimateId);
  if (!processed || processed.alternateItems.length === 0) {
    throw new ValidationError("Load an estimate before generating a report.");
  }

  await ensureStylesExist();

  const alternates = (processed.estimateData.alternates || []) as unknown as LineItem[];
  const hasFilters = hasAlternateFilters(options);
  const filteredAlternates = hasFilters ? alternates.filter((alt) => alternatePassesFilters(alt, options)) : alternates;
  const sortedAlternates = [...filteredAlternates].sort((a, b) =>
    (a.key ?? "").localeCompare(b.key ?? "", undefined, { numeric: true })
  );

  const alternateGroups = sortedAlternates.map((alt) => ({
    ...alt,
    name: codeLabel(alt.key, alt.label, options.showCodesColumns),
  }));
  await handleDynamicColumns(worksheet, columnNames.alternates, alternateGroups, {
    wrapText: true,
    copyFormat: true,
  });

  let alternateLineItems = processed.alternateItems.filter((item) => item.type === "line_item");

  if (hasFilters) {
    alternateLineItems = filterLineItemsByAlternates(alternateLineItems, sortedAlternates);
  }

  const floorGroups = processed.estimateData.floor_groups || [];
  const useGroups = transformUseGroups(processed.estimateData.estimate.stats.useGroups || [], floorGroups);
  useGroups.sort((a, b) => a.order - b.order);
  floorGroups.sort((a, b) => a.label.localeCompare(b.label));

  if (!options.groupBy || !options.freezeGroupLevels) {
    options.groupBy = await getSOVGroupings(estimateId);
  }

  const columnMap = await createColumnNameToIndexMap(worksheet);
  const usePreservedValues = await shouldUsePreservedValues(worksheet, columnMap, estimateId, options.groupBy);
  let preserved: PreservedContext | undefined;
  if (usePreservedValues) {
    const keyColumnIndices = getPreservedKeyColumnIndices(columnMap, options.groupBy);
    const values = await getPreservedColumnValues(worksheet, rowNames.alternatesSOV, columnMap, keyColumnIndices);
    preserved = { values, keyColumnIndices };
  }

  const alternatesSOVDataArray = alternatesSOVarray({
    alternateItems: alternateLineItems,
    alternates: sortedAlternates,
    columnMap,
    options,
    useGroups,
    floorGroups,
    preserved,
  });
  const startColumnIndex = columnMap.startColumnIndex;

  await printFormattedData({
    dataArray: alternatesSOVDataArray,
    worksheet,
    insertAtRangeName: rowNames.alternatesSOV,
    startColumnIndex,
    options,
  });

  await worksheetCache.setOptions(worksheet, {
    groupBy: options.groupBy,
    parentId: estimateId,
  });

  await updateReportDate(worksheet);
  await setPrintAreaToUsedRange(worksheet);
  await worksheet.context.sync();

  track("excel.createReport.alternatesSOV", {
    options: options,
    loadTime: (performance.now() - startTime) / 1000,
  });
};

interface AlternatesSOVarrayArgs {
  alternateItems: LineItem[];
  alternates: LineItem[];
  columnMap: ColumnNameToIndexMap;
  options: AlternatesSOVReportOptions;
  useGroups?: ReportUseGroup[];
  floorGroups?: FloorGroup[];
  preserved?: PreservedContext;
}

export const alternatesSOVarray = ({
  alternateItems,
  alternates,
  columnMap,
  options,
  useGroups = [],
  floorGroups = [],
  preserved,
}: AlternatesSOVarrayArgs) => {
  // Add dynamic columns to columnMap
  let firstAlternateColumnIndex = columnMap.alternates;
  const alternatesColumnIndices: Record<string, number> = {};

  alternates.forEach((alt: LineItem) => {
    const columnIndex = firstAlternateColumnIndex++;
    const alternateName = `${alt.key} - ${alt.label}`;
    columnMap[alternateName] = columnIndex;
    alternatesColumnIndices[alt.id] = columnIndex;
  });

  // Aggregate totals per group and alternate
  const aggregationMap = new Map<
    string,
    { groupCode: string; groupDesc: string; alternateTotals: { [key: string]: number } }
  >();

  const { groupBy, splitByUse } = options;
  if (splitByUse) {
    alternateItems = splitLineItemsForSOV(alternateItems, useGroups, floorGroups, groupBy);
  }

  // Use group and building rows have no _code/_desc fields; the name is the value
  const isDimensionGroupBy = groupBy === "use_group" || groupBy === "floor_group";
  const isLabelGroupBy = groupBy.startsWith("label_");
  const sortFieldCode = isDimensionGroupBy ? groupBy : `${groupBy}_code`;
  const sortFieldDescription = isDimensionGroupBy ? groupBy : `${groupBy}_desc`;

  for (const item of alternateItems) {
    const groupCode = item[sortFieldCode] || BLANK_LABEL;
    const groupDesc = item[sortFieldDescription] || BLANK_LABEL;
    // For label sort fields, use the combined value as the aggregation key
    // so rows with the same inherited code but different descriptions stay separate
    const aggKey = isLabelGroupBy ? item[groupBy] || BLANK_LABEL : groupCode;
    const alternateKey = item.alternate || BLANK_LABEL;
    const total = options.allocateMarkups ? Number(item.total_plus_markup) || 0 : Number(item.total) || 0;

    if (!aggregationMap.has(aggKey)) {
      aggregationMap.set(aggKey, { groupCode, groupDesc, alternateTotals: {} });
    }

    const entry = aggregationMap.get(aggKey)!;
    entry.alternateTotals[alternateKey] = (Number(entry.alternateTotals[alternateKey]) || 0) + total;
  }

  // Sort, ensuring blank group is last
  const useGroupOrderMap = groupBy === "use_group" ? new Map(useGroups.map((ug) => [ug.name, ug.order])) : undefined;
  const sortedEntries = Array.from(aggregationMap.entries()).sort(([a], [b]) => {
    if (a === BLANK_LABEL) return 1;
    if (b === BLANK_LABEL) return -1;
    if (useGroupOrderMap) {
      const orderA = useGroupOrderMap.get(a);
      const orderB = useGroupOrderMap.get(b);
      if (orderA !== undefined && orderB !== undefined) return orderA - orderB;
      if (orderA !== undefined) return -1;
      if (orderB !== undefined) return 1;
    }
    return a.localeCompare(b);
  });
  const columnCount = getReportColumnCount(columnMap);
  const mappedArray: (string | number | null)[][] = sortedEntries.map(([_, data]) => {
    const row: (string | number | null)[] = Array(columnCount).fill(null);
    // Use group and building rows carry their name as the code
    const rowCode = isDimensionGroupBy || data.groupCode === BLANK_LABEL ? "" : data.groupCode;
    row[columnMap.code] = isDimensionGroupBy ? "" : data.groupCode;
    row[columnMap.name] = codeLabel(rowCode, data.groupDesc, options.showCodes);

    // Populate alternate totals
    alternates.forEach((alt) => {
      const columnIndex = alternatesColumnIndices[alt.id];
      const alternateName = `${alt.key} - ${alt.label}`;
      row[columnIndex] = data.alternateTotals[alternateName] || 0;
    });

    if (preserved) {
      applyPreservedValuesToRow(row, columnMap, preserved);
    }

    return row;
  });

  return mappedArray;
};
